import { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'

// Client add-ons: screens one person paid for, found by scanning src/addons/<key>/index.jsx so
// adding one never edits a core file. See src/addons/README.md.
//
// LAZY ON PURPOSE. Each add-on's code becomes its own chunk, fetched only when the signed-in
// person has it switched on. An eager import would put every client's code in everybody's
// browser bundle, which defeats keeping one client's feature away from the others.
// A puzzle piece, for an add-on's tab or menu entry that did not bring its own icon.
export const ADDON_ICON = 'M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z'

const entryLoaders = import.meta.glob('../../addons/*/index.{js,jsx}')
const loaderByKey = {}
Object.keys(entryLoaders).forEach((path) => { loaderByKey[path.split('/')[3]] = entryLoaders[path] })

const loaded = new Map()   // key -> the add-on's default export, or null if it failed to load
const pending = new Map()

function loadAddon(key) {
  if (loaded.has(key)) return Promise.resolve(loaded.get(key))
  if (!pending.has(key)) {
    pending.set(key, loaderByKey[key]()
      .then((m) => { loaded.set(key, m.default || null) })
      .catch((e) => { console.error(`Add-on ${key} failed to load`, e); loaded.set(key, null) }))
  }
  return pending.get(key)
}

export const isAddonLoaded = (key) => loaded.has(key)

// The add-ons this person has switched on AND whose screens exist AND have loaded. The server
// is what really refuses; this only stops us drawing what it would not answer for.
export function useActiveAddons() {
  const { user } = useAuth()
  const on = Object.keys(user?.addons || {})
    .filter((k) => user.addons[k] === true && loaderByKey[k]).sort()
  const dep = on.join(',')
  const [, bump] = useState(0)
  useEffect(() => {
    let live = true
    dep.split(',').filter(Boolean).forEach((k) => loadAddon(k).then(() => live && bump((n) => n + 1)))
    return () => { live = false }
  }, [dep])
  return on.filter((k) => loaded.get(k)).map((k) => ({ key: k, mod: loaded.get(k) }))
}

// What an add-on registered into a slot, as a list of { component, label?, id?, icon? }.
// It may give one thing or a list, and a bare component stands for { component }.
export function entriesOf(mod, slot) {
  const raw = mod?.[slot]
  const list = Array.isArray(raw) ? raw : raw ? [raw] : []
  return list.map((e) => (typeof e === 'function' ? { component: e } : e)).filter((e) => e && typeof e === 'object')
}

export function useSlotEntries(slot) {
  return useActiveAddons().flatMap(({ key, mod }) =>
    entriesOf(mod, slot).map((e, i) => ({ ...e, addon: key, id: e.id || String(i) })))
}
